import React, { useMemo } from 'react';

import backSvg from '@assets/img/icons/back.svg';
import { WarehouseCalendarTable } from '@components/admin/agency/WarehouseCalendarTable';
import { WarehouseSelector } from '@components/admin/agency/WarehouseSelector';
import { TextInput } from '@components/admin/Forms';
import { Layout } from '@components/admin/Layout';
import { SectionTitle } from '@components/admin/SectionTitle';
import { calendarStrings } from '@data/calendar';
import { useAgency } from '@hooks/queries/dashboard/useAgency';
import { useWarehouse } from '@hooks/queries/dashboard/useWarehouse';
import { useWarehouses } from '@hooks/queries/dashboard/useWarehouses';
import { useUser } from '@hooks/queries/useUser';
import { useSearch } from '@hooks/useSearch';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { getBreadcrumb, url } from '@utils/pages';
import { iota } from '@utils/types';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { Form, Formik } from 'formik';
import * as t from 'io-ts';
import { useHistory } from 'react-router-dom';
import * as h from 'tyrann-io';

const strings = createStrings({
  ja: {
    title: '倉庫カレンダー',
    selectDatePrompt: '日付を選択してください。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  mainContainer: {
    ...common.flexInline,
    columnGap: '1rem',
    padding: 12,
  },
}));

const searchType = t.type({
  date: h.omittable(h.string()),
  warehouseId: h.omittable(h.number().cast()),
  agencyId: h.omittable(h.number().cast()),
  from: h.omittable(h.string()),
});

interface WarehouseCalendarPageProps {
  isAdmin: boolean;
}

export const WarehouseCalendarPage: React.FC<WarehouseCalendarPageProps> = (props) => {
  const { isAdmin } = props;
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { search, updateSearch } = useSearch(searchType);
  const { date: inputDate = new Date().toISOString() } = search;
  const warehouse = useWarehouse(search.warehouseId!, { enabled: !!search.warehouseId });
  const user = useUser();
  const agencyId = search.agencyId ?? user.data?.agency_id;
  const warehouses = useWarehouses({ agency_id: agencyId! }, { enabled: !!agencyId });
  const warehouseIdList = warehouses.data?.map((warehouse) => warehouse.id) ?? [];
  const agency = useAgency(agencyId!, { enabled: !!agencyId });
  const history = useHistory();

  const date = dayjs(inputDate);

  const breadcrumb = useMemo(
    () =>
      isAdmin
        ? [
            ...getBreadcrumb('/admin/widgets/Agencies'),
            {
              url: `${url('/admin/widgets/AgencyDetails')}?id=${agencyId}`,
              name: agency.data?.name_en ?? String(agencyId),
              title: agency.data?.name_en ?? String(agencyId),
              isDirectory: false,
            },
            strings.title,
          ]
        : getBreadcrumb('/admin/agency/WarehouseCalendar'),
    [agencyId, agency.data?.name_en, isAdmin],
  );

  const renderTitle = () => (
    <Formik
      enableReinitialize
      initialValues={{
        year: String(date.get('year')),
        month: String(date.get('month') + 1),
      }}
      validate={(values) => {
        const nextDate = new Date(Number(values.year), Number(values.month) - 1);
        if (!date.isSame(nextDate, 'month')) {
          updateSearch({
            date: nextDate.toISOString(),
          });
        }
      }}
      onSubmit={() => {}}
    >
      <SectionTitle
        actions={
          <Form>
            <div className={clsx(classes.horizontalSpace1, classes.flexInline)}>
              <TextInput
                name="year"
                choose
                noLabel
                options={iota(new Date().getFullYear() - 2022 + 2, 2022).map((y) => ({
                  value: String(y),
                  label: String(y),
                }))}
                style={{
                  width: 90,
                }}
              />

              <TextInput
                name="month"
                choose
                noLabel
                options={iota(12).map((i) => ({
                  value: String(i + 1),
                  label: calendarStrings.months(i + 1),
                }))}
                style={{
                  width: 90,
                }}
              />
            </div>
          </Form>
        }
      >
        {search.from && (
          <img
            src={backSvg}
            alt="back"
            className={classes.baseButton}
            style={{ height: 25, marginRight: '1rem' }}
            onClick={() => history.push(search.from!)}
          />
        )}
        <time dateTime={`${date.year()}-${date.month() + 1}`} data-testid="CalendarDate">
          {calendarStrings.yearMonth(date.toDate())}
        </time>
        <span>假期 {warehouse.data?.name || ''}</span>
      </SectionTitle>
    </Formik>
  );

  return (
    <Layout
      title={`${calendarStrings.yearMonth(date.toDate())} - ${strings.title}`}
      breadcrumb={breadcrumb}
    >
      <div className={classes.container}>
        {renderTitle()}
        <div className={classes.mainContainer}>
          <div style={{ flex: 1 }}>
            <WarehouseCalendarTable
              date={date.toDate()}
              agencyId={agencyId ?? undefined}
              warehouseId={search.warehouseId ?? undefined}
              warehouseIdList={warehouseIdList}
              editable={!isAdmin}
            />
          </div>
          <div className={classes.flexColumn} style={{ width: 260, paddingTop: 28 }}>
            <WarehouseSelector
              agencyId={agencyId ?? undefined}
              warehouseIdList={warehouseIdList}
              onClick={(warehouseId) => updateSearch({ ...search, warehouseId })}
              onGoBack={() => updateSearch({ ...search, warehouseId: undefined })}
              isEditing={!!search.warehouseId}
            />
          </div>
        </div>
      </div>
    </Layout>
  );
};
